import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { MapPin, Trash2, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface SavedReport {
  id: string;
  address: string;
  latitude: number;
  longitude: number;
  max_hail_inches: number | null;
  max_wind_mph: number | null;
  storm_count: number | null;
  last_storm_date: string | null;
  created_at: string;
}

export function SavedReportsTab() {
  const { orgId } = useAuthContext();
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    load();
  }, [orgId]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('storm_address_reports')
      .select('id,address,latitude,longitude,max_hail_inches,max_wind_mph,storm_count,last_storm_date,created_at')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    else setReports((data || []) as SavedReport[]);
    setLoading(false);
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from('storm_address_reports').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); load(); }
  };

  if (loading) return <Card><CardContent className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></CardContent></Card>;
  if (reports.length === 0) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No saved reports yet. Run an address report and save it.</CardContent></Card>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {reports.map(r => (
        <Card key={r.id}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-sm flex items-start gap-1"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span className="line-clamp-2">{r.address}</span></div>
              <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-orange-500/10 p-2"><div className="text-xs text-muted-foreground">Max Hail</div><div className="font-bold text-orange-600 dark:text-orange-400">{r.max_hail_inches?.toFixed(2) ?? '0'}"</div></div>
              <div className="rounded-lg bg-yellow-500/10 p-2"><div className="text-xs text-muted-foreground">Max Wind</div><div className="font-bold text-yellow-700 dark:text-yellow-400">{r.max_wind_mph ?? 0} mph</div></div>
              <div className="rounded-lg bg-red-500/10 p-2"><div className="text-xs text-muted-foreground">Events</div><div className="font-bold text-red-600 dark:text-red-400">{r.storm_count ?? 0}</div></div>
            </div>
            <div className="text-xs text-muted-foreground flex items-center justify-between">
              <span>Last storm: {r.last_storm_date || 'N/A'}</span>
              <span>Saved: {format(new Date(r.created_at), 'MMM d')}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
